import { useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import * as Sentry from "@sentry/react";
import { supabase } from "@/lib/supabase";
import { onEvent } from "@/lib/events";
import type { SpaceMemberRole } from "@/lib/supabase";

export async function resolveActiveSpaceRole(userId: string): Promise<SpaceMemberRole | null> {
  const [profileResult, ownedSpaceResult] = await Promise.all([
    supabase.from("profiles").select("active_space_id").eq("id", userId).single(),
    supabase.from("spaces").select("id").eq("owner_id", userId).is("deleted_at", null).maybeSingle(),
  ]);

  const activeSpaceId =
    (profileResult.data as { active_space_id: string | null } | null)?.active_space_id ?? null;
  const ownedSpaceId = (ownedSpaceResult.data as { id: string } | null)?.id ?? null;

  // No active_space_id or viewing their own space → owner
  if (!activeSpaceId || activeSpaceId === ownedSpaceId) return "owner";

  // Guest in another space — look up the membership role
  const { data: memberData } = await supabase
    .from("space_members")
    .select("role")
    .eq("user_id", userId)
    .eq("space_id", activeSpaceId)
    .eq("status", "active")
    .maybeSingle();

  return (memberData as { role: SpaceMemberRole } | null)?.role ?? null;
}

// ── Module-level role cache ─────────────────────────────────────────────────
// Every useAuth() caller reads `activeSpaceRole` from this shared cache so
// remounted consumers (HomeFeed, Profile, MemoryDetailSheet, MobileShell)
// see the correct role on their first render — no flash-of-adult-UI for
// child/contributor accounts while a per-instance fetch is in flight.

type RoleCacheEntry = { role: SpaceMemberRole | null; ts: number };
const ROLE_TTL = 5 * 60 * 1000;
const roleCache = new Map<string, RoleCacheEntry>();
const roleInFlight = new Map<string, Promise<SpaceMemberRole | null>>();
const roleSubscribers = new Set<() => void>();

function notifyRoleSubscribers(): void {
  for (const cb of roleSubscribers) cb();
}

function getCachedRole(userId: string): SpaceMemberRole | null | undefined {
  const hit = roleCache.get(userId);
  if (!hit) return undefined;
  if (Date.now() - hit.ts > ROLE_TTL) return undefined;
  return hit.role;
}

function fetchActiveSpaceRole(userId: string): Promise<SpaceMemberRole | null> {
  const existing = roleInFlight.get(userId);
  if (existing) return existing;
  const p = resolveActiveSpaceRole(userId)
    .then((role) => {
      roleCache.set(userId, { role, ts: Date.now() });
      roleInFlight.delete(userId);
      notifyRoleSubscribers();
      return role;
    })
    .catch((err) => {
      roleInFlight.delete(userId);
      throw err;
    });
  roleInFlight.set(userId, p);
  return p;
}

/** Seed the role cache from App.tsx's boot probe so the first render of
 *  every useAuth consumer reads the role synchronously. */
export function seedActiveSpaceRoleCache(userId: string, role: SpaceMemberRole | null): void {
  roleCache.set(userId, { role, ts: Date.now() });
  notifyRoleSubscribers();
}

/** Drop the cached role — call after a space switch or anything else that
 *  may change which role the user is operating under. */
export function invalidateActiveSpaceRoleCache(userId: string): void {
  roleCache.delete(userId);
  roleInFlight.delete(userId);
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [, bumpRole] = useState(0);

  useEffect(() => {
    // Hydrate from existing session first so there's no flash on reload
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        Sentry.setUser({ id: session.user.id });
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        Sentry.setUser({ id: session.user.id });
      } else {
        Sentry.setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Subscribe to the shared role cache — any cache update re-renders all
  // useAuth consumers in lockstep.
  useEffect(() => {
    const cb = () => bumpRole((n) => n + 1);
    roleSubscribers.add(cb);
    return () => {
      roleSubscribers.delete(cb);
    };
  }, []);

  // Ensure the role for the current user is in the cache. Dedup via the
  // inFlight map so concurrent consumers share a single fetch.
  useEffect(() => {
    if (!user) return;
    if (getCachedRole(user.id) !== undefined) return;
    void fetchActiveSpaceRole(user.id);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rotate cached role when the user switches spaces. The shell guards
  // (MobileShell, BottomNav, Profile, MyTribe) gate on this value, so it
  // must re-resolve every time the active space changes.
  useEffect(() => {
    if (!user) return;
    const off = onEvent("space.switched", () => {
      invalidateActiveSpaceRoleCache(user.id);
      void fetchActiveSpaceRole(user.id);
    });
    return off;
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const signOut = () => supabase.auth.signOut();

  const activeSpaceRole: SpaceMemberRole | null = user
    ? (getCachedRole(user.id) ?? null)
    : null;

  return { user, session, loading, signOut, activeSpaceRole };
}
